import { useState, useEffect } from 'react'
import { Search, Loader2, Check } from 'lucide-react'
import type { Expense } from '@/api/client'
import { api } from '@/api/client'

interface ExpenseAttachPickerProps {
  /** Expense IDs already linked to the current document (shown as linked) */
  linkedExpenseIds?: string[]
  onSelect: (expenseId: string) => void
}

export function ExpenseAttachPicker({ linkedExpenseIds = [], onSelect }: ExpenseAttachPickerProps) {
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    api.getExpenses()
      .then((res) => setExpenses(res || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const filtered = expenses.filter(e => {
    if (!search) return true
    const term = search.toLowerCase()
    return e.provider.toLowerCase().includes(term)
      || e.description.toLowerCase().includes(term)
  })

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)

  const formatDate = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 size={20} className="text-[#1B365D] animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AEC0]" />
        <input
          type="text"
          placeholder="Search expenses..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 text-[13px] border border-[#E2E8F0] rounded-lg text-[#4A5568] focus:outline-none focus:border-[#1B365D]"
        />
      </div>

      {/* Expense list */}
      <div className="max-h-60 overflow-y-auto space-y-1">
        {filtered.length === 0 && (
          <div className="text-[13px] text-[#A0AEC0] text-center py-4">
            {search ? 'No matching expenses' : 'No expenses found'}
          </div>
        )}
        {filtered.map((expense) => {
          const isLinked = linkedExpenseIds.includes(expense.id)
          return (
            <button
              key={expense.id}
              onClick={() => !isLinked && onSelect(expense.id)}
              disabled={isLinked}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                isLinked
                  ? 'border-[#10B981]/30 bg-[#10B981]/5 cursor-default'
                  : 'border-[#E2E8F0] hover:border-[#1B365D]/30 hover:bg-[#F7FAFC] cursor-pointer'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-[#1B365D] truncate">
                    {expense.provider}
                  </div>
                  <div className="text-[11px] text-[#A0AEC0] truncate">
                    {expense.description}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  <div className="text-right">
                    <div className="text-[13px] font-medium text-[#1B365D]">
                      {formatCurrency(expense.amount)}
                    </div>
                    <div className="text-[11px] text-[#A0AEC0]">
                      {formatDate(expense.serviceDate)}
                    </div>
                  </div>
                  {isLinked && (
                    <Check size={14} className="text-[#10B981]" />
                  )}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
